import { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BookOpen, Play, HelpCircle, ShieldCheck, Users2, LayoutDashboard,
  ListChecks, ClipboardList, Inbox, TrendingUp, Building2, UploadCloud,
  Mail, UserCog, Settings, Crown, Sparkles, ChevronDown, ChevronRight, MessageSquare,
} from 'lucide-react';
import { storageKey } from '../components/OnboardingTour';
import { log } from '../lib/logger';

// ─── Role → what they can actually do ───────────────────────────────────────
const ROLE_MATRIX = {
  super_user: {
    label: 'Super User',
    tagline: 'Full access — you can see and do everything.',
    color: '#685B72',
    daily: [
      "Skim the Dashboard tiles for anything red or overdue.",
      "Open Executive BI on Monday morning for the weekly performance snapshot.",
      "Approve any pending users in User Management and assign role + clinic.",
      "Answer Feedback & Bug reports from your team.",
    ],
    weekly: [
      "Upload the latest SimplePractice exports in Data Import.",
      "Review OD Bonus progress in Executive BI → Clinics tab.",
      "Check Referrals BI tab to see which sources are feeding which clinics.",
    ],
  },
  admin: {
    label: 'Admin',
    tagline: 'Full ops access — everything except the Executive BI cube and user role changes.',
    color: '#52475A',
    daily: [
      "Work the Client Pipeline — move rows through stages as paperwork/pre-auth clears.",
      "Watch Pending Notes and follow up with clinicians who are behind.",
      "Sort through Inquiries and disposition new website + fax leads.",
    ],
    weekly: [
      "Upload the latest SimplePractice reports in Data Import.",
      "Send the Weekly OD Digest email on Monday morning.",
      "Update rates or add referral sources in Setup.",
    ],
  },
  super_admin: {
    label: 'Super Admin (legacy)',
    tagline: 'Legacy role — auto-migrated to Admin on next login.',
    color: '#685B72',
    daily: [],
    weekly: [],
  },
  hr_payroll: {
    label: 'HR / Payroll',
    tagline: 'Runs payroll plus full ops access — everything except Executive BI, Admin Vault, and Underpayment Recovery.',
    color: '#B08A3A',
    daily: [
      "Track SimplePractice attendance and confirm therapist units for the current pay period.",
      "Work through pending notes, inquiries, and clinician roster updates alongside admins.",
      "Answer payroll questions and reconcile Gusto exports against Thrive's internal totals.",
    ],
    weekly: [
      "Finalize the payroll run and archive the immutable snapshot in the Payroll module.",
      "Review Clinician Performance for utilization trends that affect bonus calculations.",
      "Update Setup dropdowns or rates when new hires or contract changes come through.",
    ],
  },
  office_director: {
    label: 'Office Director',
    tagline: 'Your clinic — pipeline, performance, and inquiries for your team.',
    color: '#839E97',
    daily: [
      "Open your clinic page — see caseload, staff, and today's actions in one view.",
      "Work Inquiries scoped to your clinic — every one is a possible new client.",
      "Check Pending Notes for your team and nudge anyone who's slipping.",
    ],
    weekly: [
      "Review Clinician Performance for show-rate and utilization.",
      "Update your Clinician Roster if there are new hires or departures.",
    ],
  },
  read_only: {
    label: 'Read Only',
    tagline: 'Read/write client info across the whole org · clinic config stays view-only.',
    color: '#685B72',
    daily: [
      "Use the Dashboard and Client Pipeline to look up any client.",
      "Reference Pending Notes and clinic pages to answer questions.",
    ],
    weekly: [
      "Watch Clinician Performance if you're supporting HR / operations.",
    ],
  },
};

// ─── Feature reference cards (filtered per role) ────────────────────────────
const FEATURES = [
  {
    id: 'dashboard', icon: LayoutDashboard, title: 'Dashboard',
    what: "The morning glance — every red-flag task, referral, and pipeline step surfaced as a tile.",
    how: "Click any tile to jump straight to that filtered slice of the pipeline. Use the date-range selector at the top-right to change the window.",
    roles: 'ALL',
  },
  {
    id: 'pipeline', icon: Users2, title: 'Client Pipeline',
    what: "Every client in one searchable table with an auto-derived status column (no manual tracking).",
    how: "Search by client name. Click a row to open the full history + edit sheet. Statuses update automatically based on paperwork, pre-auth, and scheduling data.",
    roles: 'ALL',
  },
  {
    id: 'today', icon: ListChecks, title: "Today's Actions",
    what: "The subset of pipeline work that is YOURS to move today.",
    how: "Complete items → they disappear from this list. It re-orders itself as things get done.",
    roles: 'ALL',
  },
  {
    id: 'pending-notes', icon: ClipboardList, title: 'Pending Notes',
    what: "All overdue progress notes grouped by clinician with 0-30 / 30-60 / 60+ day aging.",
    how: "Sparkline shows each clinician's trailing pattern. Use this before your weekly clinician 1:1.",
    roles: 'ALL',
  },
  {
    id: 'inquiries', icon: Inbox, title: 'Inquiries',
    what: "Website leads auto-sync from Google every 30 min. Fax referrals are logged manually.",
    how: "For each lead: choose a disposition (new / contacted / no-response / duplicate / not-eligible / not-interested / converted). Click ‘Convert’ to spin up a full pipeline client row from the inquiry.",
    roles: 'ALL',
  },
  {
    id: 'clinics', icon: Building2, title: 'Clinic pages',
    what: "Everything scoped to one clinic: caseload, staff, referrals, notes owed, and clinic KPIs.",
    how: "Office Directors see only their clinic. Everyone else sees all 9 in the sidebar.",
    roles: 'ALL',
  },
  {
    id: 'performance', icon: TrendingUp, title: 'Clinician Performance',
    what: "Per-clinician show rate, session counts, note timeliness, and utilization.",
    how: "Set effective-dated targets so historical months don't get penalized when a target changes.",
    roles: ['super_user', 'admin', 'office_director'],
  },
  {
    id: 'clinicians', icon: Users2, title: 'Clinician Roster',
    what: "The master list of every clinician: credentials, home clinic, effective dates.",
    how: "ODs can only edit their own clinic's staff. Admins can edit any clinic. Read-only for staff.",
    roles: ['super_user', 'admin', 'office_director', 'read_only'],
  },
  {
    id: 'executive-bi', icon: Crown, title: 'Executive BI',
    what: "The full financial + operational cube: revenue momentum, realization, denials, payer mix, per-clinic scorecard, referrals report, and the OD Quarterly Bonus meter.",
    how: "Data source = the SimplePractice files uploaded in Data Import. Click ‘Refresh Cube’ after uploads. Note: Telehealth (Video Office) sessions are automatically re-attributed to each clinician's home clinic — you'll never see a stand-alone Telehealth tile in the Clinic Scorecard.",
    roles: ['super_user'],
  },
  {
    id: 'import', icon: UploadCloud, title: 'Data Import',
    what: "Drop SimplePractice XLSX files (Attendance, Appt Status, Claims, Income, Payer Alloc). Report type is auto-detected.",
    how: "Natural-key UPSERT — safe to re-upload the same file, duplicates are prevented at the row level. Cube auto-refreshes on the next open. The same page also hosts the Duplicate Audit and (for super_user) two emergency data tools.",
    roles: ['super_user', 'admin'],
  },
  {
    id: 'duplicate-audit', icon: Users2, title: 'Duplicate Audit',
    what: "Finds near-duplicate Clients, Inquiries, and Clinicians using fuzzy name matching (≥ 85% similarity) plus exact matches on SP client ID / email / phone.",
    how: "For each group: Merge to fold the extras into a chosen keeper (their appointments and history follow), or Ignore forever to dismiss the pair permanently — dismissed pairs never re-surface on future scans. Lives on the Data Import page.",
    roles: ['super_user', 'admin'],
  },
  {
    id: 'legacy-id-healer', icon: UploadCloud, title: 'Legacy BI Row-ID Healer',
    what: "One-click migration that rewrites any old-format BI row IDs to the current natural-key hashing scheme. Prevents BI double-counting after re-uploads.",
    how: "On the Data Import page, click ‘Normalize Legacy IDs’. Idempotent — a second click touches nothing. Safe to run any time.",
    roles: ['super_user'],
  },
  {
    id: 'bi-hard-reset', icon: UploadCloud, title: 'Hard-Reset · Wipe SP BI Data',
    what: "Nuclear option when the Legacy ID Healer isn't enough. Empties every SimplePractice-derived BI collection so you can re-import fresh from scratch.",
    how: "On the Data Import page, click ‘Wipe SP BI Data’ and type WIPE-SP-DATA to confirm. Client Pipeline, Inquiries, Clinicians, Clinics, Users, Setup, Rates, and Availability are NEVER touched. After wiping, re-upload the Appointment Status Report (and any other SP reports) using the sections below.",
    roles: ['super_user'],
  },
  {
    id: 'od-digest', icon: Mail, title: 'Weekly OD Digest',
    what: "The Monday-morning email each Office Director gets with their clinic's snapshot.",
    how: "Auto-fires every Monday 07:00 CT. You can trigger a manual test from this page.",
    roles: ['super_user', 'admin'],
  },
  {
    id: 'users', icon: UserCog, title: 'User Management',
    what: "Approve @thrivespeech.net teammates as they sign in with Google.",
    how: "New signups land in ‘pending’. Assign role + clinic(s) here to grant access.",
    roles: ['super_user'],
  },
  {
    id: 'setup', icon: Settings, title: 'Setup & Dropdowns',
    what: "Every dropdown value across the app — clinics, service types, insurance types, referral sources, etc.",
    how: "Adding a value here makes it immediately available in every form that uses it.",
    roles: ['super_user', 'admin'],
    note: 'Super Admins have full Setup access.',
  },
  {
    id: 'feedback', icon: MessageSquare, title: 'Feedback & Bugs',
    what: "One click to log any bug, request, or idea — Davinder gets an email instantly.",
    how: "Attach a screenshot if the issue is visual (10× faster fixes). When an admin marks it Resolved, the submitter gets a notification email with the resolution notes. Click the 💬 icon on any row to open a chat-style reply thread — the whole team can keep discussing in-app instead of switching to email. On Resolved rows, tap 👍 / ✅ / 🎉 to react (click the same emoji again to remove it).",
    roles: 'ALL',
  },
];

// ─── FAQ ───────────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: "I just signed in but I can't see anything. What now?",
    a: "You're probably in ‘pending’ state. Davinder (Super User) needs to approve you in User Management and assign your role + clinic. Send him a Slack/text to speed it up.",
  },
  {
    q: "How do statuses update in the Client Pipeline?",
    a: "They're derived automatically from what's happening — when paperwork is received, when pre-auth is submitted, when the eval is scheduled, etc. You don't manually pick a status; you complete the underlying step and the status changes on its own.",
  },
  {
    q: "How often do website inquiries sync?",
    a: "Every 30 minutes on the dot. If you need it immediately, click ‘Sync now’ on the Inquiries page.",
  },
  {
    q: "I uploaded a SimplePractice report but nothing changed in Executive BI.",
    a: "After uploading, click ‘Refresh Cube’ on Executive BI (or the Data Import page). The cube recomputes in a few seconds and the dashboard updates.",
  },
  {
    q: "My Executive BI numbers look doubled after a re-upload.",
    a: "That means legacy row IDs are colliding with the new natural-key hashes. On the Data Import page, click ‘Normalize Legacy IDs’. It's safe to run any time and idempotent — a second click touches nothing.",
  },
  {
    q: "I need to wipe all SimplePractice BI data and start fresh.",
    a: "On the Data Import page, super_users have a ‘Wipe SP BI Data’ button that empties every SP-derived BI collection (bi_appt_status, bi_attendance, bi_claims, bi_income, bi_payer_alloc, bi_snapshot, bi_uploads). Type WIPE-SP-DATA to confirm. Client Pipeline, Inquiries, Clinicians, Clinics, Users, Setup, and Rates are never touched. Re-upload your reports afterwards.",
  },
  {
    q: "Where do Telehealth (Video Office) sessions show up in reports?",
    a: "They're automatically re-attributed to each clinician's home clinic — measured from where they do the majority of their in-person work, or falling back to their roster's assigned clinic. You'll never see a stand-alone Telehealth tile in the Clinic Scorecard. Admins can override a clinician's home clinic on the Clinician Roster page.",
  },
  {
    q: "I clicked ‘Ignore forever’ on a duplicate but it keeps showing up.",
    a: "Refresh the Duplicate Audit — dismissed pairs are filtered on every scan. If you still see the same pair, please open a Feedback ticket with the client IDs — it may indicate a corner case.",
  },
  {
    q: "How do I keep a conversation going on a Feedback ticket?",
    a: "Click the 💬 icon on any Feedback row to open a chat-style reply thread. Anyone can post replies — no need to switch to email. On Resolved rows, tap 👍 / ✅ / 🎉 to react (same emoji again removes your reaction).",
  },
  {
    q: "Why don't I see certain sidebar tabs my colleague sees?",
    a: "The sidebar filters by role. Admins don't see Clinician Performance or Pending Notes; an Office Director sees only their assigned clinic; Read-Only can't reach Setup. This is by design — ask Davinder if you think your permissions are off.",
  },
  {
    q: "Can I re-run the walk-through?",
    a: "Yes — click ‘Restart the tour’ at the top of this page anytime.",
  },
  {
    q: "I found a bug or want a new feature.",
    a: "Use Feedback & Bugs in the sidebar. Include a screenshot if the issue is visual — Davinder gets a direct email.",
  },
];

// ─── Section shell ─────────────────────────────────────────────────────────
function Section({ title, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="font-serif text-2xl text-[#2D2833] mb-3">{title}</h2>
      {children}
    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────
export default function HelpGuide() {
  const { user } = useAuth();
  const role = user?.role || 'read_only';
  const profile = ROLE_MATRIX[role] || ROLE_MATRIX.read_only;

  const visibleFeatures = FEATURES.filter(
    (f) => f.roles === 'ALL' || (Array.isArray(f.roles) && f.roles.includes(role))
  );
  const [openFaq, setOpenFaq] = useState(null);

  const startTour = () => {
    // Clearing the localStorage flag lets Layout re-arm auto-launch on next
    // login too, but the immediate dispatch runs the tour right now.
    // Safe to swallow the error — localStorage can throw in Safari private
    // mode or when quota is exceeded; failure just means the flag stays set
    // (worst case: user gets the auto-tour again next login).
    try { if (user?.email) localStorage.removeItem(storageKey(user.email)); }
    catch (err) { log.warn('localStorage unavailable — tour flag not cleared:', err); }
    window.dispatchEvent(new CustomEvent('tour:start'));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-2" data-testid="help-page">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Help & Guide</div>
        <h1 className="font-serif text-4xl text-[#2D2833] mt-1">How to use Thrive Ops Hub</h1>
        <p className="text-[#685B72] mt-2 max-w-2xl">
          A quick reference for your role — daily flow, weekly tasks, and every feature you can touch.
          Bookmark this page.
        </p>
      </div>

      {/* Restart tour + role callout */}
      <Card
        className="rounded-2xl p-5 mb-8 border flex flex-col md:flex-row md:items-center gap-4"
        style={{ borderColor: '#E8E5EC', background: 'linear-gradient(135deg,#FBF9FC 0%,#F5EFF7 100%)' }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} style={{ color: profile.color }} />
            <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: profile.color }}>
              Signed in as {profile.label}
            </span>
          </div>
          <div className="font-serif text-lg text-[#2D2833]">{profile.tagline}</div>
        </div>
        <Button
          onClick={startTour}
          className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl px-4"
          data-testid="restart-tour-btn"
        ><Play size={14} className="mr-2" /> Restart the 30-second tour</Button>
      </Card>

      {/* Your Day + Your Week */}
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        <Card className="rounded-2xl p-6 border" style={{ borderColor: '#E8E5EC' }} data-testid="help-your-day">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#685B72]" />
            <h3 className="font-serif text-lg text-[#2D2833]">Your day</h3>
          </div>
          <ul className="space-y-2.5">
            {profile.daily.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm text-[#685B72] leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: profile.color }} />
                {s}
              </li>
            ))}
          </ul>
        </Card>
        <Card className="rounded-2xl p-6 border" style={{ borderColor: '#E8E5EC' }} data-testid="help-your-week">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-[#685B72]" />
            <h3 className="font-serif text-lg text-[#2D2833]">Your week</h3>
          </div>
          <ul className="space-y-2.5">
            {profile.weekly.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm text-[#685B72] leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: profile.color }} />
                {s}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Feature reference */}
      <Section title="Every feature you can touch" testid="help-features">
        <div className="grid md:grid-cols-2 gap-3">
          {visibleFeatures.map((f) => (
            <Card key={f.id} className="rounded-2xl p-5 border" style={{ borderColor: '#E8E5EC' }} data-testid={`help-feature-${f.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <f.icon size={16} className="text-[#685B72]" />
                <h3 className="font-serif text-lg text-[#2D2833]">{f.title}</h3>
              </div>
              <div className="text-sm text-[#685B72] leading-relaxed mb-2"><b className="text-[#52475A]">What it is: </b>{f.what}</div>
              <div className="text-sm text-[#685B72] leading-relaxed"><b className="text-[#52475A]">How to use it: </b>{f.how}</div>
              {f.note && (
                <div className="mt-2 text-[11px] text-[#685B72]/80 italic border-t border-[#F1EDF5] pt-2">{f.note}</div>
              )}
            </Card>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section title="Frequently asked" testid="help-faq">
        <div className="space-y-2">
          {FAQ.map((f, i) => (
            <Card
              key={i}
              className="rounded-2xl border cursor-pointer overflow-hidden"
              style={{ borderColor: '#E8E5EC' }}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`help-faq-${i}`}
            >
              <div className="p-4 flex items-center gap-3">
                {openFaq === i ? <ChevronDown size={16} className="text-[#685B72]" /> : <ChevronRight size={16} className="text-[#685B72]" />}
                <div className="font-medium text-[#2D2833] text-sm flex-1">{f.q}</div>
              </div>
              {openFaq === i && (
                <div className="px-4 pb-4 pl-11 text-sm text-[#685B72] leading-relaxed">{f.a}</div>
              )}
            </Card>
          ))}
        </div>
      </Section>

      {/* Footer note */}
      <div className="pt-4 flex items-center gap-3 text-sm text-[#685B72]/80">
        <HelpCircle size={16} />
        <span>Still stuck? Use <b>Feedback & Bugs</b> in the sidebar — it goes straight to Davinder.</span>
      </div>
    </div>
  );
}
